import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { Alert } from '@material-ui/lab'
import {
    getStateMedicaidIngestStatus,
    triggerStateMedicaidIngest,
    uploadStateMedicaidIngest,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'
import COVERAGE_ROSTER from './coverage'

// Visual styling per tier for the placeholder rows (states we haven't shipped yet).
const TIER_STYLE = {
    buildable: { label: 'BUILDABLE',   color: 'var(--badge-watch-ink)', bg: 'var(--badge-watch-bg)', border: '#f0d3a2' },
    gated:     { label: 'GATED',       color: '#a73b3b', bg: '#fdecea', border: '#f5b6b0' },
    frozen:    { label: 'FROZEN',      color: '#5b6270', bg: '#eef0f4', border: '#c9cdd6' },
    leie:      { label: 'LEIE-ONLY',   color: '#6c7076', bg: '#f0f1f3', border: '#d7d9dd' },
}

const statusChipStyle = (status) => {
    switch (status) {
        case 'SUCCESS':          return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'RUNNING':          return { background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }
        case 'FAILED':           return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        case 'SKIPPED_NOT_MODIFIED': return { background: '#f3e5f5', color: '#6a1b9a' }
        default:                 return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

const formatTs = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

// Date of the last successful load plus how long ago, e.g. "10/14/2026 · 3d ago".
const formatUpdated = (iso) => {
    if (!iso) return 'Never'
    const d = new Date(iso)
    const days = Math.floor((Date.now() - d.getTime()) / 86400000)
    return `${d.toLocaleDateString()} · ${days <= 0 ? 'today' : `${days}d ago`}`
}

const formatNum = (n) => (n == null ? '—' : n.toLocaleString?.() ?? n)

// Sort priority for the default (health-first) view: real failures / running float to the top,
// SKIPPED and successful runs sit below. Manual states with a FAILED scheduler run are demoted —
// their failure is expected (portal can't be auto-pulled), so they shouldn't outrank an actual
// broken state. Anything else (no run yet) drops last.
const STATUS_PRIORITY = { FAILED: 0, RUNNING: 1, SUCCESS: 2, SKIPPED_NOT_MODIFIED: 3 }
const statusRank = (status, manual = false) => {
    if (manual && status === 'FAILED') return 2.5     // between healthy and never-run
    return status in STATUS_PRIORITY ? STATUS_PRIORITY[status] : 99
}

// Compare helper used by TableSortLabel — returns +/-1 based on a getter and direction.
const compareBy = (getter, direction) => (a, b) => {
    const va = getter(a)
    const vb = getter(b)
    if (va == null && vb == null) return 0
    if (va == null) return 1        // nulls last
    if (vb == null) return -1
    if (va < vb) return direction === 'asc' ? -1 : 1
    if (va > vb) return direction === 'asc' ? 1 : -1
    return 0
}

const COLUMNS = [
    { id: 'stateCode',       label: 'State',   width: 68,   align: 'left',  get: (s) => s.stateCode },
    { id: 'status',          label: 'Status',  width: 170,  align: 'left',  get: (s) => statusRank(s.lastRun?.status, s.manual) },
    { id: 'totalRecords',    label: 'Total',   width: 100,  align: 'right', get: (s) => s.totalRecords ?? 0 },
    { id: 'activeRecords',   label: 'Active',  width: 100,  align: 'right', get: (s) => s.activeRecords ?? 0 },
    { id: 'lastSuccessAt',   label: 'Last updated', width: 170, align: 'left', get: (s) => s.lastSuccessAt || null },
    { id: 'elapsed',         label: 'Elapsed', width: 90,   align: 'right', get: (s) => s.lastRun?.durationMs ?? null },
    { id: 'finishedAt',      label: 'Last run',width: 190,  align: 'left',  get: (s) => s.lastRun?.finishedAt || s.lastRun?.startedAt || null },
]

const StatusChip = ({ status, running }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Chip label={status ?? 'NEVER RUN'} size='small' style={statusChipStyle(status)} />
        {running && <CircularProgress size={12} />}
    </span>
)

// A single state expands to a detail block below its main row.
const StateDetailRow = ({ state, colSpan }) => {
    const lastRun = state.lastRun
    return (
        <TableRow>
            <TableCell colSpan={colSpan} style={{ padding: '12px 24px 20px 60px', background: 'var(--bg-canvas)', borderTop: '1px solid #eee' }}>
                {state.placeholder && (
                    <div style={{
                        marginBottom: 12,
                        padding: '10px 12px',
                        borderLeft: '3px solid #5f6c7b',
                        background: '#eef1f5',
                        fontSize: 13,
                        color: '#37414d',
                    }}>
                        <strong>Placeholder.</strong> {state.displayName || state.stateCode}
                        {' '}publishes no downloadable bulk file — the data lives only on the
                        state portal (HTML table, per-record PDFs, or login-walled). This row
                        exists so the portal link stays discoverable; the ⬆ Upload file action
                        is disabled because there is nothing standard to upload. Automating
                        would require a scraper (not built).
                        {state.portalUrl && (
                            <div style={{ marginTop: 8 }}>
                                <a
                                    href={state.portalUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{
                                        display: 'inline-block',
                                        padding: '4px 10px',
                                        background: 'var(--fg-secondary)',
                                        color: '#fff',
                                        textDecoration: 'none',
                                        borderRadius: 3,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        letterSpacing: '.02em',
                                    }}
                                >
                                    ↗ Open {state.stateCode} portal
                                </a>
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#556170' }}>
                                    {state.portalUrl}
                                </span>
                            </div>
                        )}
                        {state.notes && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#37414d' }}>
                                <strong>Note:</strong> {state.notes}
                            </div>
                        )}
                    </div>
                )}
                {state.manual && !state.placeholder && (
                    <div style={{
                        marginBottom: 12,
                        padding: '10px 12px',
                        borderLeft: '3px solid #a25a1a',
                        background: '#fff8e1',
                        fontSize: 13,
                        color: '#5c3a12',
                    }}>
                        <strong>Manual state.</strong> {state.displayName || state.stateCode}'s
                        source portal can't be auto-downloaded (blocks non-state egress, uses an
                        ASP.NET postback for the actual file). Scheduler failures on this row are
                        expected — fetch the file from the state portal in a browser and use the
                        <strong> ⬆ Upload file </strong> action. The daily digest email reminds
                        you when the last successful upload is older than the staleness threshold.
                        {state.portalUrl && (
                            <div style={{ marginTop: 8 }}>
                                <a
                                    href={state.portalUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{
                                        display: 'inline-block',
                                        padding: '4px 10px',
                                        background: 'var(--badge-watch-ink)',
                                        color: '#fff',
                                        textDecoration: 'none',
                                        borderRadius: 3,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        letterSpacing: '.02em',
                                    }}
                                >
                                    ↗ Open {state.stateCode} portal
                                </a>
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#7a5518' }}>
                                    {state.portalUrl}
                                </span>
                            </div>
                        )}
                        {state.notes && (
                            <div style={{
                                marginTop: 8,
                                padding: '6px 10px',
                                background: 'var(--badge-watch-bg)',
                                border: '1px solid #f0d3a2',
                                borderRadius: 3,
                                fontSize: 12,
                                color: '#5c3a12',
                            }}>
                                <strong>⚠ Note:</strong> {state.notes}
                            </div>
                        )}
                    </div>
                )}
                {/* Non-manual states with notes get a subtler blue-tinted callout — same info,
                    less alarm, since these states auto-download fine from the deployed envs. */}
                {!state.manual && state.notes && (
                    <div style={{
                        marginBottom: 12,
                        padding: '8px 12px',
                        borderLeft: '3px solid #1976d2',
                        background: 'var(--badge-review-bg)',
                        fontSize: 12,
                        color: '#0d3c61',
                    }}>
                        <strong>⚠ Note:</strong> {state.notes}
                    </div>
                )}
                {!lastRun && <span style={{ fontSize: 13, color: '#666' }}>No ingest run recorded for this state yet.</span>}
                {lastRun && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13 }}>
                        <strong>Started:</strong><span>{formatTs(lastRun.startedAt)}</span>
                        <strong>Finished:</strong><span>{formatTs(lastRun.finishedAt)}</span>
                        <strong>Elapsed:</strong><span>{lastRun.elapsed ?? '—'}</span>
                        <strong>Records upserted:</strong><span>{formatNum(lastRun.recordsUpserted)}</span>
                        <strong>Records removed:</strong><span>{formatNum(lastRun.recordsRemoved)}</span>
                        <strong>Reinstated (in table):</strong><span>{formatNum(state.reinstatedRecords)}</span>
                        {lastRun.rowsRead != null && (<><strong>Rows read:</strong><span>{formatNum(lastRun.rowsRead)}</span></>)}
                        {lastRun.rowsDedupSkipped != null && (<><strong>Rows skipped (dedup):</strong><span>{formatNum(lastRun.rowsDedupSkipped)}</span></>)}
                        {lastRun.rowsMalformedSkipped != null && (<><strong>Rows skipped (malformed):</strong><span>{formatNum(lastRun.rowsMalformedSkipped)}</span></>)}
                        {lastRun.sourceFilename && (<><strong>Source file:</strong><span style={{ wordBreak: 'break-word' }}>{lastRun.sourceFilename}</span></>)}
                        {lastRun.sourceLastModified && (<><strong>Source Last-Modified:</strong><span>{lastRun.sourceLastModified}</span></>)}
                        {lastRun.errorMessage && (
                            <>
                                <strong style={{ color: 'var(--status-declined)' }}>Error:</strong>
                                <span style={{ color: 'var(--status-declined)', wordBreak: 'break-word' }}>{lastRun.errorMessage}</span>
                            </>
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    )
}

const StateRow = ({ state, expanded, selected, disableSelect, triggering, onToggleExpand, onToggleSelect, onRun, onUpload }) => {
    const lastRun = state.lastRun
    const isRunning = lastRun?.status === 'RUNNING'
    const fileInputRef = useRef(null)
    // Several files can be picked at once (MN publishes orgs + individuals as two files); the
    // backend routes each one to its source by headers, so they're uploaded one after another.
    const onFileSelected = async (event) => {
        const files = Array.from(event.target.files || [])
        event.target.value = ''
        for (const file of files) {
            await onUpload(state.stateCode, file)
        }
    }
    // Explicit expand handler on the arrow and the state code — the row-level onClick was flaky,
    // so we bind directly to the two spots where a click most obviously means "expand this row"
    // and skip the row-wide handler entirely.
    const toggleExpand = () => onToggleExpand(state.stateCode)

    return (
        <>
            <TableRow hover>
                <TableCell padding='checkbox'>
                    <Checkbox
                        size='small'
                        checked={selected}
                        onChange={() => onToggleSelect(state.stateCode)}
                        disabled={disableSelect}
                        inputProps={{ 'aria-label': `select ${state.stateCode}` }}
                    />
                </TableCell>
                <TableCell padding='none' style={{ width: 32 }}>
                    <IconButton size='small' aria-label='expand row' onClick={toggleExpand}>
                        {expanded ? <KeyboardArrowDownIcon fontSize='small' /> : <KeyboardArrowRightIcon fontSize='small' />}
                    </IconButton>
                </TableCell>
                <TableCell
                    onClick={toggleExpand}
                    style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{state.stateCode}</span>
                        {state.placeholder && (
                            <Tooltip title="Placeholder — state publishes no bulk file. Row exists so the portal link stays discoverable; upload is disabled because there is nothing to upload.">
                                <Chip
                                    label='PLACEHOLDER'
                                    size='small'
                                    style={{
                                        background: '#eef1f5',
                                        color: '#37414d',
                                        border: '1px solid #c9d1db',
                                        fontSize: 10,
                                        letterSpacing: '.05em',
                                        fontWeight: 700,
                                        height: 18,
                                    }}
                                />
                            </Tooltip>
                        )}
                        {state.manual && !state.placeholder && (
                            <Tooltip title="Manual state — the source portal can't be auto-downloaded (e.g. blocks non-state egress, uses an ASP.NET postback download). Fetch the file in a browser and use ⬆ Upload file. Scheduler will keep failing on this state; that's expected. Digest email will remind you when the file goes stale.">
                                <Chip
                                    label='MANUAL'
                                    size='small'
                                    style={{
                                        background: 'var(--badge-watch-bg)',
                                        color: 'var(--badge-watch-ink)',
                                        border: '1px solid #f0d3a2',
                                        fontSize: 10,
                                        letterSpacing: '.05em',
                                        fontWeight: 700,
                                        height: 18,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                </TableCell>
                <TableCell><StatusChip status={lastRun?.status} running={isRunning} /></TableCell>
                <TableCell align='right' style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNum(state.totalRecords)}</TableCell>
                <TableCell align='right' style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNum(state.activeRecords)}</TableCell>
                <TableCell style={{ color: '#555', fontSize: 12, whiteSpace: 'nowrap' }}>{formatUpdated(state.lastSuccessAt)}</TableCell>
                <TableCell align='right' style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{lastRun?.elapsed ?? '—'}</TableCell>
                <TableCell style={{ color: '#555', fontSize: 12 }}>{formatTs(lastRun?.finishedAt || lastRun?.startedAt)}</TableCell>
                <TableCell align='right' style={{ whiteSpace: 'nowrap' }}>
                    {/* Manual states can't be auto-downloaded — only the Upload action is offered.
                        Hiding the traps entirely is cleaner than a disabled+tooltip dance, and the
                        MANUAL badge on the state cell already explains why. */}
                    {!state.manual && (
                        <>
                            <Tooltip title='Check + ingest (HEAD first)'>
                                <span>
                                    <IconButton size='small' color='primary' onClick={() => onRun(state.stateCode, false)} disabled={triggering || isRunning}>
                                        <PlayArrowIcon fontSize='small' />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title='Force re-download'>
                                <span>
                                    <IconButton size='small' onClick={() => onRun(state.stateCode, true)} disabled={triggering || isRunning}>
                                        <CloudDownloadIcon fontSize='small' />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {!state.placeholder && (
                        <Tooltip title={state.manual ? 'Upload the file you fetched from the state portal (CSV / TSV / XLSX) — the only path that works for manual states.' : 'Upload local file (CSV / TSV / XLSX)'}>
                            <span>
                                <IconButton size='small' color={state.manual ? 'primary' : 'default'} onClick={() => fileInputRef.current?.click()} disabled={triggering || isRunning}>
                                    <CloudUploadIcon fontSize='small' />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.csv,.txt,.tsv,.xlsx,.pdf,text/csv,text/plain,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf'
                        multiple
                        onChange={onFileSelected}
                        style={{ display: 'none' }}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={10} style={{ padding: 0, border: 0 }}>
                    <Collapse in={expanded} timeout='auto' unmountOnExit>
                        <Table size='small'>
                            <TableBody>
                                <StateDetailRow state={state} colSpan={10} />
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const StateMedicaidUsage = () => {
    const [ingest, setIngest] = useState(null)
    const [loading, setLoading] = useState(false)
    const [triggering, setTriggering] = useState(false)
    const [expanded, setExpanded] = useState(() => new Set())
    const [selected, setSelected] = useState(() => new Set())
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [orderBy, setOrderBy] = useState('status')
    const [order, setOrder] = useState('asc')

    const fetch = useCallback(() => {
        setLoading(true)
        getStateMedicaidIngestStatus()()
            .then((status) => setIngest(status))
            .catch(() => Toast({ type: 'error', message: 'Failed to load state Medicaid status' }))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { fetch() }, [fetch])

    const anyRunning = useMemo(
        () => (ingest?.states || []).some((s) => s.lastRun?.status === 'RUNNING'),
        [ingest]
    )
    useEffect(() => {
        if (!anyRunning) return undefined
        const interval = setInterval(fetch, 15000)
        return () => clearInterval(interval)
    }, [anyRunning, fetch])

    const rawStates = ingest?.states || []

    // Filter → search → sort. Health-first sort is the default so operators see FAILED / RUNNING
    // before healthy rows without having to click any header.
    const filteredStates = useMemo(() => {
        const q = search.trim().toUpperCase()
        return rawStates.filter((s) => {
            // "Needs attention" excludes manual states with an expected FAILED — those are known
            // ops overhead, not real failures. A running scheduler on a manual state (rare) still
            // qualifies as "in flight and worth watching".
            const status = s.lastRun?.status
            const isRealFailure = status === 'FAILED' && !s.manual
            if (statusFilter === 'NEEDS_ATTENTION' && !isRealFailure && status !== 'RUNNING') return false
            if (statusFilter === 'FAILED' && status !== 'FAILED') return false
            if (statusFilter === 'MANUAL' && !s.manual) return false
            if (q && !s.stateCode?.toUpperCase().includes(q)) return false
            return true
        })
    }, [rawStates, search, statusFilter])

    const sortedStates = useMemo(() => {
        const col = COLUMNS.find((c) => c.id === orderBy) ?? COLUMNS[1]
        return [...filteredStates].sort(compareBy(col.get, order))
    }, [filteredStates, orderBy, order])

    const toggleExpand = (code) => {
        setExpanded((prev) => {
            const next = new Set(prev)
            next.has(code) ? next.delete(code) : next.add(code)
            return next
        })
    }

    const toggleSelect = (code) => {
        setSelected((prev) => {
            const next = new Set(prev)
            next.has(code) ? next.delete(code) : next.add(code)
            return next
        })
    }

    // Rows exposed to the header select-all: skip in-flight runs AND manual states — bulk force
    // re-download is meaningless on a state that can't be auto-pulled. A user can still tick a
    // manual row individually (with the same intent as clicking the per-row action), but the
    // bulk action will skip it before firing to avoid pointless failures — see bulkForceReDownload.
    const selectableStates = useMemo(
        () => sortedStates.filter((s) => s.lastRun?.status !== 'RUNNING' && !s.manual),
        [sortedStates]
    )
    const allSelectedVisible = selectableStates.length > 0 && selectableStates.every((s) => selected.has(s.stateCode))
    const someSelectedVisible = selectableStates.some((s) => selected.has(s.stateCode)) && !allSelectedVisible

    const toggleSelectAll = () => {
        setSelected((prev) => {
            if (allSelectedVisible) {
                const next = new Set(prev)
                selectableStates.forEach((s) => next.delete(s.stateCode))
                return next
            }
            const next = new Set(prev)
            selectableStates.forEach((s) => next.add(s.stateCode))
            return next
        })
    }

    const runIngest = (stateCode, force) => {
        setTriggering(true)
        triggerStateMedicaidIngest({ state: stateCode, force })()
            .then(() => {
                Toast({
                    type: 'success',
                    message: force
                        ? `Ingest started for ${stateCode} (force re-download)`
                        : `Ingest started for ${stateCode}`,
                })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409
                    ? `An ingest is already running for ${stateCode}`
                    : `Failed to trigger ingest for ${stateCode}`
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const uploadForState = (stateCode, file) => {
        setTriggering(true)
        return uploadStateMedicaidIngest({ state: stateCode, file })()
            .then(() => {
                Toast({ type: 'success', message: `${stateCode} ingest started from ${file.name}` })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409
                    ? `An ingest is already running for ${stateCode}`
                    : `Failed to upload file for ${stateCode}`
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const runAllStates = () => {
        setTriggering(true)
        triggerStateMedicaidIngest({})()
            .then(() => {
                Toast({ type: 'success', message: 'Ingest started for all registered states' })
                fetch()
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to trigger ingest' }))
            .finally(() => setTriggering(false))
    }

    // Bulk force re-download — fire the per-state calls in parallel. Manual states are stripped
    // before dispatch (they'd fail deterministically), so the user gets a clean summary rather
    // than a sea of red toasts, and one toast per state doesn't drown them either.
    const bulkForceReDownload = () => {
        const requested = Array.from(selected)
        if (requested.length === 0) return
        const manualCodes = new Set(rawStates.filter((s) => s.manual).map((s) => s.stateCode))
        const codes = requested.filter((c) => !manualCodes.has(c))
        const skippedManual = requested.length - codes.length
        if (codes.length === 0) {
            Toast({ type: 'error', message: `Selection is manual-only — use ⬆ Upload file on manual rows` })
            return
        }
        setTriggering(true)
        Promise.allSettled(
            codes.map((code) => triggerStateMedicaidIngest({ state: code, force: true })())
        ).then((results) => {
            const ok = results.filter((r) => r.status === 'fulfilled').length
            const failed = results.length - ok
            let summary = failed === 0
                ? `Force re-download started for ${ok} state(s)`
                : `Force re-download started for ${ok} of ${results.length} — ${failed} failed`
            if (skippedManual > 0) summary += ` · skipped ${skippedManual} manual state(s)`
            Toast({ type: failed === 0 ? 'success' : 'error', message: summary })
            setSelected(new Set())
            fetch()
        }).finally(() => setTriggering(false))
    }

    const handleSort = (columnId) => {
        if (orderBy === columnId) {
            setOrder(order === 'asc' ? 'desc' : 'asc')
        } else {
            setOrderBy(columnId)
            setOrder('asc')
        }
    }

    const failingCount = rawStates.filter((s) => s.lastRun?.status === 'FAILED').length
    const runningCount = rawStates.filter((s) => s.lastRun?.status === 'RUNNING').length
    // Real failures = FAILED, excluding manual states (whose scheduled runs are expected to fail).
    const realFailingCount = rawStates.filter((s) => s.lastRun?.status === 'FAILED' && !s.manual).length
    const manualCount = rawStates.filter((s) => s.manual).length

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>State Medicaid Exclusions Usage</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        startIcon={<PlayArrowIcon />}
                        onClick={runAllStates}
                        disabled={triggering || anyRunning || rawStates.length === 0}
                        variant='outlined'
                        size='small'
                        color='primary'
                    >
                        Ingest all states
                    </Button>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={fetch}
                        disabled={loading}
                        variant='outlined'
                        size='small'
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            <Alert severity='info' className='mb-3'>
                Each state Medicaid agency publishes its own suspended-provider list on its own cadence
                (typically monthly). The scheduler checks each state daily and only re-downloads when
                the source has changed — unchanged days record <strong>SKIPPED_NOT_MODIFIED</strong>{' '}
                in ~1&nbsp;second per state. States badged{' '}
                <span style={{
                    background: 'var(--badge-watch-bg)', color: 'var(--badge-watch-ink)', border: '1px solid #f0d3a2',
                    padding: '1px 6px', borderRadius: 3, fontSize: 11, fontWeight: 700, letterSpacing: '.05em',
                }}>MANUAL</span>{' '}
                can't be auto-downloaded — fetch the file in a browser and use the upload action.
            </Alert>

            {loading && !ingest && (
                <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
            )}

            {ingest && rawStates.length === 0 && (
                <Alert severity='warning'>
                    No state Medicaid sources are registered. Add a{' '}
                    <code>StateMedicaidSource</code> implementation in <code>ds-state-medicaid</code>{' '}
                    and restart the API.
                </Alert>
            )}

            {rawStates.length > 0 && (
                <>
                    {/* Toolbar: search + filter chips + selection summary */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                        <TextField
                            size='small'
                            variant='outlined'
                            placeholder='Filter by state code (e.g. CA)'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ minWidth: 220 }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                            <Chip
                                label={`All · ${rawStates.length}`}
                                onClick={() => setStatusFilter('ALL')}
                                variant={statusFilter === 'ALL' ? 'default' : 'outlined'}
                                color={statusFilter === 'ALL' ? 'primary' : 'default'}
                                size='small'
                            />
                            <Chip
                                label={`Needs attention · ${realFailingCount + runningCount}`}
                                onClick={() => setStatusFilter('NEEDS_ATTENTION')}
                                variant={statusFilter === 'NEEDS_ATTENTION' ? 'default' : 'outlined'}
                                style={statusFilter === 'NEEDS_ATTENTION' ? { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' } : {}}
                                size='small'
                            />
                            <Chip
                                label={`Failed · ${failingCount}`}
                                onClick={() => setStatusFilter('FAILED')}
                                variant={statusFilter === 'FAILED' ? 'default' : 'outlined'}
                                style={statusFilter === 'FAILED' ? { background: 'var(--status-declined)', color: '#fff' } : {}}
                                size='small'
                            />
                            {manualCount > 0 && (
                                <Tooltip title="Manual states — the source portal can't be auto-downloaded (blocks non-state egress or uses an ASP.NET postback). Fetch manually and use ⬆ Upload file.">
                                    <Chip
                                        label={`Manual · ${manualCount}`}
                                        onClick={() => setStatusFilter('MANUAL')}
                                        variant={statusFilter === 'MANUAL' ? 'default' : 'outlined'}
                                        style={statusFilter === 'MANUAL'
                                            ? { background: 'var(--badge-watch-ink)', color: '#fff' }
                                            : { borderColor: '#f0d3a2', color: 'var(--badge-watch-ink)' }}
                                        size='small'
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <div style={{ flex: 1 }} />
                        {selected.size > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 12px', background: 'var(--badge-review-bg)', borderRadius: 4 }}>
                                <span style={{ fontSize: 13, color: 'var(--rox-lilac-ink)' }}>
                                    <strong>{selected.size}</strong> selected
                                </span>
                                <Button
                                    startIcon={<CloudDownloadIcon />}
                                    variant='contained'
                                    color='primary'
                                    size='small'
                                    disabled={triggering}
                                    onClick={bulkForceReDownload}
                                >
                                    Force re-download selected
                                </Button>
                                <Button size='small' onClick={() => setSelected(new Set())} disabled={triggering}>
                                    Clear
                                </Button>
                            </div>
                        )}
                    </div>

                    <TableContainer component={Paper} variant='outlined'>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding='checkbox'>
                                        <Checkbox
                                            size='small'
                                            checked={allSelectedVisible}
                                            indeterminate={someSelectedVisible}
                                            onChange={toggleSelectAll}
                                            disabled={selectableStates.length === 0}
                                            inputProps={{ 'aria-label': 'select all states' }}
                                        />
                                    </TableCell>
                                    <TableCell padding='none' style={{ width: 32 }} />
                                    {COLUMNS.map((col) => (
                                        <TableCell key={col.id} align={col.align} style={{ width: col.width, fontWeight: 600 }}>
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell align='right' style={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedStates.map((state) => (
                                    <StateRow
                                        key={state.stateCode}
                                        state={state}
                                        expanded={expanded.has(state.stateCode)}
                                        selected={selected.has(state.stateCode)}
                                        disableSelect={state.lastRun?.status === 'RUNNING' || !!state.manual}
                                        triggering={triggering}
                                        onToggleExpand={toggleExpand}
                                        onToggleSelect={toggleSelect}
                                        onRun={runIngest}
                                        onUpload={uploadForState}
                                    />
                                ))}
                                {sortedStates.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#666' }}>
                                            No states match the current filter.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <CoveragePlaceholders liveStateCodes={rawStates.map((s) => s.stateCode)} />

                    <div style={{ marginTop: 16, padding: '12px 16px', border: '1px solid #e0e0e0', borderRadius: 4, fontSize: 13, lineHeight: 1.8 }}>
                        <div><strong>Coverage:</strong> {rawStates.map((s) => s.stateCode).join(', ')}</div>
                        <div><strong>Cost:</strong> free — public state open-data portals, no API keys required</div>
                        <div><strong>Scheduler:</strong> daily check (<code>roxwrite.scheduler.state-medicaid-ingest.cron</code>)</div>
                        <div><strong>Cadence:</strong> states publish monthly; daily check is cheap thanks to per-state Last-Modified short-circuit</div>
                    </div>
                </>
            )}
        </>
    )
}

/**
 * Rest-of-the-country coverage — the 47 states we haven't shipped yet, grouped by tier. Collapsed
 * by default (operators mostly care about the live table above; this section is planning context).
 * Filters out any roster row whose state code has actually been shipped since the const was last
 * updated so we don't render two entries for the same state.
 */
const CoveragePlaceholders = ({ liveStateCodes }) => {
    const [expanded, setExpanded] = useState(false)
    const live = new Set(liveStateCodes)
    const rows = COVERAGE_ROSTER.filter((r) => !live.has(r.stateCode))
    const counts = rows.reduce((acc, r) => {
        acc[r.tier] = (acc[r.tier] || 0) + 1
        return acc
    }, {})

    return (
        <div style={{ marginTop: 24 }}>
            <button
                type='button'
                onClick={() => setExpanded((v) => !v)}
                style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 4,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#333',
                }}
            >
                {expanded ? <KeyboardArrowDownIcon fontSize='small' /> : <KeyboardArrowRightIcon fontSize='small' />}
                <span>Rest of the country · {rows.length} state{rows.length === 1 ? '' : 's'}</span>
                <span style={{ color: '#666', fontWeight: 400 }}>
                    ({counts.buildable || 0} buildable · {counts.gated || 0} gated · {counts.frozen || 0} frozen · {counts.leie || 0} LEIE-only)
                </span>
            </button>
            <Collapse in={expanded} timeout='auto' unmountOnExit>
                <TableContainer component={Paper} variant='outlined' style={{ marginTop: 8, opacity: 0.85 }}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600, width: 60 }}>ST</TableCell>
                                <TableCell style={{ fontWeight: 600, width: 130 }}>Tier</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Agency / list</TableCell>
                                <TableCell align='right' style={{ fontWeight: 600, width: 100 }}>Est. rows</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Notes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {['buildable', 'gated', 'frozen', 'leie'].flatMap((tier) => {
                                const tierRows = rows.filter((r) => r.tier === tier)
                                if (tierRows.length === 0) return []
                                return tierRows.map((r) => {
                                    const style = TIER_STYLE[r.tier]
                                    return (
                                        <TableRow key={r.stateCode} style={{ background: '#fff' }}>
                                            <TableCell style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, color: '#555' }}>
                                                {r.stateCode}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={style.label}
                                                    size='small'
                                                    style={{
                                                        background: style.bg,
                                                        color: style.color,
                                                        border: `1px solid ${style.border}`,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        letterSpacing: '.04em',
                                                        height: 20,
                                                    }}
                                                />
                                                {r.barrier && (
                                                    <span style={{
                                                        marginLeft: 6,
                                                        fontSize: 11,
                                                        color: '#666',
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    }}>
                                                        {r.barrier}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell style={{ color: '#444' }}>{r.agency}</TableCell>
                                            <TableCell align='right' style={{
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontVariantNumeric: 'tabular-nums',
                                                color: r.rowsEstimate == null ? '#999' : '#444',
                                            }}>
                                                {r.rowsEstimate == null ? '—' : r.rowsEstimate.toLocaleString()}
                                            </TableCell>
                                            <TableCell style={{ fontSize: 12, color: '#666' }}>
                                                {r.notes || ''}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div style={{ marginTop: 8, fontSize: 12, color: '#666', padding: '0 4px' }}>
                    Row counts from OpenSanctions US Medicaid census (Dec 2024). Buildable = feed reachable
                    programmatically, ~2.5 dev-days per state. Gated = feed exists but requires human-driven
                    fetch (CAPTCHA, login, PDF). LEIE-only = state publishes no independent feed; federal LEIE covers them.
                </div>
            </Collapse>
        </div>
    )
}

export default StateMedicaidUsage
